import React, { useState, useEffect, useCallback } from 'react';
import { useCache } from '../hooks/useCache';
import AnalysisShortcutBar from '../components/AnalysisShortcutBar';
import ViewModeToggle from '../components/ViewModeToggle';
import { useViewMode } from '../hooks/useViewMode';
import '../components/ExpandableList.css';
import './Filters.css';

function Filters() {
  const cache = useCache();

  const [filters, setFilters] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editing, setEditing] = useState(null);
  const [filterName, setFilterName] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useViewMode('filters');
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = useCallback((id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [filtersRes, hierRes] = await Promise.all([
        cache.getSavedFilters(),
        cache.getCachedFacilityHierarchy()
      ]);
      setFilters(filtersRes?.filters || []);
      setHierarchy(hierRes?.hierarchy || []);
    } catch {
      setError('Failed to load data. Make sure facilities have been synced.');
    } finally {
      setLoading(false);
    }
  }, [cache]);

  useEffect(() => { loadData(); }, []);

  const startCreate = () => {
    setEditing('new');
    setFilterName('');
    setSelectedIds(new Set());
    setExpandedNodes(new Set());
    setSearchTerm('');
  };

  const startEdit = (filter) => {
    setEditing(filter.id);
    setFilterName(filter.name);
    setSelectedIds(new Set(filter.facilityIds || []));
    setExpandedNodes(new Set());
    setSearchTerm('');
  };

  const cancelEdit = () => {
    setEditing(null);
    setFilterName('');
    setSelectedIds(new Set());
  };

  const handleSave = async () => {
    if (!filterName.trim()) { setError('Filter name is required'); return; }
    if (selectedIds.size === 0) { setError('Select at least one facility'); return; }

    setSaving(true);
    setError('');
    try {
      const ids = Array.from(selectedIds);
      if (editing === 'new') {
        await cache.createFilter(filterName.trim(), ids);
      } else {
        await cache.updateFilter(editing, filterName.trim(), ids);
      }
      setEditing(null);
      setFilterName('');
      setSelectedIds(new Set());
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save filter');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this filter?')) return;
    try {
      await cache.deleteFilter(id);
      await loadData();
    } catch {
      setError('Failed to delete filter');
    }
  };

  const getAllDescendantFacilityIds = (node) => {
    const ids = [];
    if (node.vitalEngine_Facility_ID__c) {
      ids.push(Math.round(parseFloat(node.vitalEngine_Facility_ID__c)).toString());
    }
    if (node.children?.length > 0) {
      node.children.forEach(child => ids.push(...getAllDescendantFacilityIds(child)));
    }
    return ids;
  };

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId);
      return next;
    });
  };

  const handleCheck = (node) => {
    const ids = getAllDescendantFacilityIds(node);
    setSelectedIds(prev => {
      const next = new Set(prev);
      const anySelected = ids.some(id => next.has(id));
      if (anySelected) {
        ids.forEach(id => next.delete(id));
      } else {
        ids.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const isChecked = (node) => {
    const ids = getAllDescendantFacilityIds(node);
    return ids.length > 0 && ids.every(id => selectedIds.has(id));
  };

  const isIndeterminate = (node) => {
    const ids = getAllDescendantFacilityIds(node);
    if (ids.length === 0) return false;
    const count = ids.filter(id => selectedIds.has(id)).length;
    return count > 0 && count < ids.length;
  };

  const matchesSearch = (node) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    if (node.name?.toLowerCase().includes(s)) return true;
    if (node.vitalEngine_Facility_ID__c?.toString().includes(s)) return true;
    if (node.billingCity?.toLowerCase().includes(s)) return true;
    if (node.billingState?.toLowerCase().includes(s)) return true;
    if (node.children?.length > 0) return node.children.some(c => matchesSearch(c));
    return false;
  };

  const renderNode = (node, level = 0) => {
    if (!matchesSearch(node)) return null;
    const hasChildren = node.children?.length > 0;
    const isExp = expandedNodes.has(node.id);
    const checked = isChecked(node);
    const indet = isIndeterminate(node);
    const childCount = hasChildren ? node.children.length : 0;

    return (
      <div key={node.id} className="ft-node" style={{ paddingLeft: `${level * 1.5}rem` }}>
        <div className="ft-node-row">
          {hasChildren ? (
            <button className="ft-expand" onClick={() => toggleNode(node.id)}>
              {isExp ? '▼' : '▶'}
            </button>
          ) : <span className="ft-spacer" />}

          <label className="ft-label">
            <input
              type="checkbox"
              checked={checked}
              ref={el => { if (el) el.indeterminate = indet; }}
              onChange={() => handleCheck(node)}
            />
            <span className={`ft-name ${hasChildren ? 'ft-parent' : ''}`}>{node.name}</span>
            {childCount > 0 && (
              <span className="ft-badge">{childCount}</span>
            )}
          </label>

          {node.billingCity && (
            <span className="ft-location">{node.billingCity}, {node.billingState}</span>
          )}
        </div>
        {hasChildren && isExp && node.children.map(c => renderNode(c, level + 1))}
      </div>
    );
  };

  const selectAll = () => {
    const all = new Set();
    hierarchy.forEach(node => getAllDescendantFacilityIds(node).forEach(id => all.add(id)));
    setSelectedIds(all);
  };

  const facilityCountForFilter = (filter) => {
    return filter.facilityIds?.length || 0;
  };

  const resolveFacilityNames = (facilityIds) => {
    if (!facilityIds || !hierarchy.length) return [];
    const map = new Map();
    const walk = (nodes) => {
      nodes.forEach(n => {
        if (n.vitalEngine_Facility_ID__c) {
          map.set(Math.round(parseFloat(n.vitalEngine_Facility_ID__c)).toString(), n.name);
        }
        if (n.children) walk(n.children);
      });
    };
    walk(hierarchy);
    return facilityIds.map(id => map.get(id) || `ID ${id}`);
  };

  if (loading) {
    return (
      <div className="filters-page">
        <div className="filters-loading">
          <div className="spinner" />
          <p>Loading filters...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="filters-page">
      <div className="filters-header">
        <div>
          <h2>My Filters</h2>
          <p className="filters-subtitle">
            Your private named facility groups for quick selection when analyzing data.
            Pin the ones you want on your Home dashboard.
          </p>
        </div>
        {editing === null && (
          <div className="filters-header-actions">
            <ViewModeToggle mode={viewMode} onChange={setViewMode} />
            <button className="filters-create-btn" onClick={startCreate}>
              + New Filter
            </button>
          </div>
        )}
      </div>

      {error && <div className="filters-error">{error}</div>}

      {editing !== null && (
        <div className="filter-editor">
          <div className="filter-editor-header">
            <h3>{editing === 'new' ? 'Create New Filter' : 'Edit Filter'}</h3>
          </div>

          <div className="filter-editor-name">
            <label htmlFor="filterName">Filter Name</label>
            <input
              id="filterName"
              type="text"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder="e.g. Northeast Hospitals, Top 10 Sites..."
              autoFocus
            />
          </div>

          <div className="filter-editor-selection">
            <div className="filter-editor-toolbar">
              <span className="selection-count">
                {selectedIds.size} {selectedIds.size === 1 ? 'facility' : 'facilities'} selected
              </span>
              <div className="filter-editor-actions">
                <button className="ft-link-btn" onClick={selectAll}>Select All</button>
                <button className="ft-link-btn" onClick={() => setSelectedIds(new Set())}>Clear</button>
              </div>
            </div>

            <div className="filter-editor-search">
              <input
                type="text"
                placeholder="Search by name, ID, city, or state..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="ft-clear-search" onClick={() => setSearchTerm('')}>×</button>
              )}
            </div>

            <div className="filter-editor-tree">
              {hierarchy.length > 0 ? (
                hierarchy.map(node => renderNode(node))
              ) : (
                <p className="ft-empty">No facilities loaded. Sync facilities from the Analytics page first.</p>
              )}
            </div>
          </div>

          <div className="filter-editor-footer">
            <button className="ft-cancel-btn" onClick={cancelEdit} disabled={saving}>Cancel</button>
            <button className="ft-save-btn" onClick={handleSave} disabled={saving || !filterName.trim() || selectedIds.size === 0}>
              {saving ? 'Saving...' : (editing === 'new' ? 'Create Filter' : 'Save Changes')}
            </button>
          </div>
        </div>
      )}

      {editing === null && (
        <div className="filters-list">
          {filters.length === 0 ? (
            <div className="filters-empty">
              <p>No saved filters yet.</p>
              <p>Create a filter to quickly apply facility selections when viewing analytics data.</p>
            </div>
          ) : viewMode === 'list' ? (
            <div className="expandable-list filters-expandable-list">
              {filters.map(filter => {
                const isExpanded = expandedIds.has(filter.id);
                const count = facilityCountForFilter(filter);
                return (
                  <div
                    key={filter.id}
                    className={`expandable-list-item ${isExpanded ? 'is-expanded' : ''}`}
                  >
                    <div
                      className="expandable-list-row"
                      onClick={() => toggleExpanded(filter.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleExpanded(filter.id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <button
                        type="button"
                        className="expandable-list-expand"
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                        onClick={(e) => { e.stopPropagation(); toggleExpanded(filter.id); }}
                      >
                        {isExpanded ? '▼' : '▶'}
                      </button>
                      <div className="expandable-list-title">
                        <span className="expandable-list-title-text">{filter.name}</span>
                      </div>
                      <span className="expandable-list-row-meta">
                        {count} {count === 1 ? 'facility' : 'facilities'}
                      </span>
                    </div>
                    {isExpanded && (
                      <div className="expandable-list-details">
                        <FilterCardBody
                          filter={filter}
                          facilityCount={count}
                          facilityNames={resolveFacilityNames(filter.facilityIds)}
                          onEdit={() => startEdit(filter)}
                          onDelete={() => handleDelete(filter.id)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            filters.map(filter => (
              <div key={filter.id} className="filter-card">
                <FilterCardBody
                  filter={filter}
                  facilityCount={facilityCountForFilter(filter)}
                  facilityNames={resolveFacilityNames(filter.facilityIds)}
                  onEdit={() => startEdit(filter)}
                  onDelete={() => handleDelete(filter.id)}
                  showHeader
                />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function FilterCardBody({ filter, facilityCount, facilityNames, onEdit, onDelete, showHeader = false }) {
  return (
    <>
      {showHeader && (
        <div className="filter-card-header">
          <h4 className="filter-card-name">{filter.name}</h4>
          <div className="filter-card-actions">
            <button type="button" className="filter-card-btn edit" onClick={onEdit}>Edit</button>
            <button type="button" className="filter-card-btn delete" onClick={onDelete}>Delete</button>
          </div>
        </div>
      )}
      {!showHeader && (
        <div className="filter-card-actions filter-card-actions-inline">
          <button type="button" className="filter-card-btn edit" onClick={onEdit}>Edit</button>
          <button type="button" className="filter-card-btn delete" onClick={onDelete}>Delete</button>
        </div>
      )}
      <div className="filter-card-meta">
        <span className="filter-card-count">{facilityCount} facilities</span>
        <span className="filter-card-date">Updated {new Date(filter.updatedAt).toLocaleDateString()}</span>
      </div>
      <div className="filter-card-facilities">
        {facilityNames.slice(0, 8).map((name, i) => (
          <span key={i} className="filter-tag">{name}</span>
        ))}
        {filter.facilityIds?.length > 8 && (
          <span className="filter-tag filter-tag-more">+{filter.facilityIds.length - 8} more</span>
        )}
      </div>
      <AnalysisShortcutBar
        context={{ kind: 'filter', id: filter.id, label: filter.name }}
        stopPropagation={false}
      />
    </>
  );
}

export default Filters;
